import { auth } from '../firebase'

const LOG_ENDPOINT = '/api/log'
const SESSION_KEY = 'bb_session_id'
const MAX_QUEUE = 50
const FLUSH_MAX = 25
const FLUSH_INTERVAL_MS = 5000

let installed = false
let queue = []
let flushing = false
let flushTimer = null
let lastPath = ''
let tokenCache = { token: null, exp: 0 }

function nowMs() {
  return Date.now()
}

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY)
    if (!id) {
      id = `bb_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
      localStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return `bb_${Math.random().toString(36).slice(2, 10)}`
  }
}

function truncate(value, max = 1000) {
  if (value == null) return value
  const str = String(value)
  return str.length > max ? `${str.slice(0, max)}…` : str
}

function safeUrl(input) {
  try {
    const url = new URL(input, window.location.origin)
    return `${url.origin}${url.pathname}`
  } catch {
    return truncate(input, 300)
  }
}

async function getToken() {
  const u = auth.currentUser
  if (!u) return null
  const now = nowMs()
  if (tokenCache.token && tokenCache.exp > now + 60_000) return tokenCache.token
  try {
    const t = await u.getIdToken()
    tokenCache = { token: t, exp: now + 5 * 60_000 }
    return t
  } catch {
    return null
  }
}

// Auth v2: current operator (set by OperatorContext) stamped on every event.
let currentOperatorName = null
export function setBlackboxOperator(name) {
  currentOperatorName = name || null
}

function enqueue(event) {
  const payload = {
    ...event,
    ts: nowMs(),
    sessionId: getSessionId(),
    operator: currentOperatorName || undefined,
    path: window.location.pathname + window.location.search + window.location.hash,
    ua: navigator.userAgent
  }
  queue.push(payload)
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE)
  }
  if (queue.length >= FLUSH_MAX) flush()
}

async function flush() {
  if (flushing || queue.length === 0) return
  flushing = true
  const events = queue.splice(0, FLUSH_MAX)
  try {
    const token = await getToken()
    if (!token) {
      queue = events.concat(queue)
      return
    }
    await fetch(LOG_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events, sessionId: getSessionId() })
    })
  } catch {
    queue = events.concat(queue)
  } finally {
    flushing = false
  }
}

function trackRouteChange() {
  const current = window.location.pathname + window.location.search + window.location.hash
  if (!lastPath) {
    lastPath = current
    enqueue({ type: 'session_start', level: 'info', path: current })
    return
  }
  if (current !== lastPath) {
    enqueue({ type: 'route', level: 'info', from: lastPath, to: current })
    lastPath = current
  }
}

export function installBlackbox() {
  if (installed) return
  installed = true

  lastPath = window.location.pathname + window.location.search + window.location.hash
  trackRouteChange()

  // Fetch wrapper
  const origFetch = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || ''
    if (url.includes(LOG_ENDPOINT)) return origFetch(input, init)
    const method = (init.method || (typeof input !== 'string' ? input?.method : 'GET') || 'GET').toUpperCase()
    const start = performance.now()
    try {
      const res = await origFetch(input, init)
      const durationMs = Math.round(performance.now() - start)
      enqueue({
        type: 'fetch',
        level: res.ok ? 'info' : 'warn',
        method,
        url: safeUrl(url),
        status: res.status,
        durationMs
      })
      return res
    } catch (err) {
      const durationMs = Math.round(performance.now() - start)
      enqueue({
        type: 'fetch_error',
        level: 'error',
        method,
        url: safeUrl(url),
        durationMs,
        message: truncate(err?.message || 'Fetch error', 2000),
        stack: truncate(err?.stack || '', 4000)
      })
      throw err
    }
  }

  // History hooks
  const origPush = history.pushState
  const origReplace = history.replaceState
  history.pushState = function (...args) {
    origPush.apply(this, args)
    trackRouteChange()
  }
  history.replaceState = function (...args) {
    origReplace.apply(this, args)
    trackRouteChange()
  }
  window.addEventListener('popstate', trackRouteChange)

  // Global errors
  window.addEventListener('error', (event) => {
    enqueue({
      type: 'error',
      level: 'error',
      message: truncate(event.message, 2000),
      stack: truncate(event.error?.stack || '', 4000)
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    enqueue({
      type: 'unhandledrejection',
      level: 'error',
      message: truncate(reason?.message || String(reason), 2000),
      stack: truncate(reason?.stack || '', 4000)
    })
  })

  // Flush interval + before unload
  flushTimer = window.setInterval(flush, FLUSH_INTERVAL_MS)
  window.addEventListener('beforeunload', flush)
}

export function logEvent(event) {
  enqueue({ type: 'custom', level: 'info', ...event })
}
